import '../MapPopupTable/MapPopupTable.css'
import temperatureIcon from '../../assets/icons/popup-temperature.svg'
import pressureIcon from '../../assets/icons/popup-pressure.svg'
import visibilityIcon from '../../assets/icons/popup-visibility.svg'
import windIcon from '../../assets/icons/popup-wind.svg'
import ceilingIcon from '../../assets/icons/popup-ceiling.svg'
import heightIcon from '../../assets/icons/heightIcon.svg'
import { useEffect, useState } from 'react'
import { fetchUserThresholds } from '../CustomThresholds/thresholdsApi'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { addSensorEvent, deleteSensorEvent, eventBodyTemplate, sensorHazardProductMap } from '../Alerting/AlertingSlice/AlertingSlice'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { ErrorTimeouts } from '../../constants/ErrorTimeouts'
import { getActiveHazards } from '../../utils/SensorAlertsUtils'
import { useSensorEventContext } from '../../utils/SensorAlertsUtils'

const MapPopupTable = ( { 
                          properties = {}, 
                          latitude = 0, 
                          longitude = 0, 
                          alertFeature = false, 
                          activeEvent = null
                        }) => {
  let timeStampVal: string = "NA";
  let sensorIdVal: string = "NA";
  let temperatureVal: string = "NA";
  let ceilingVal: string = "NA";
  let windVal: string = "NA";
  let dirVal: string = "NA";
  let visibilityVal: string = "NA";
  let sensoryTypeVal: string = "NA";
  let dewpointVal: string = "NA";
  let pressureVal: string = "NA";
  let additionalImageURL: string = "NA";
  let rawObservationVal: string = "NA";
  let prunedKeys: Set<string> = new Set();
  let isAviationProduct : boolean = false;
  let aglVal : string = "NA"
  let averageRate : string = "NA"

  // Groups for each variable
  const keysToVariables = {
    timestamp: { keys: ["timestamp"], variable: "timeStampVal", defaultValue: "NA", unitKey: "units" },
    sensorId: { keys: ["StationID", "IMEINumberModemID"], variable: "sensorIdVal", defaultValue: "NA", unitKey: "" },
    temperature: { keys: ["Temperature"], variable: "temperatureVal", defaultValue: "NA", unitKey: "units" },
    ceiling: { keys: ["Ceiling"], variable: "ceilingVal", defaultValue: "NA", unitKey: "units" },
    wind: { keys: ["WindSpeed", "Wind Speed"], variable: "windVal", defaultValue: "NA", unitKey: "units" },
    direction: { keys: ["WindDirection", "Wind Direction"], variable: "dirVal", defaultValue: "NA", unitKey: "units" },
    visibility: { keys: ["Visibility"], variable: "visibilityVal", defaultValue: "NA", unitKey: "units" },
    sensorType: { keys: ["SensorType", "Report Type", "airepType"], variable: "sensoryTypeVal", defaultValue: "NA", unitKey: "" },
    dewPoint: { keys: ["DewPointTemperature", "Dew Point Temperature", "DewpointTemperature", "DewPoint"], variable: "dewpointVal", defaultValue: "NA", unitKey: "units" },
    pressure: { keys: ["Altimeter", "Pressure", "StationPressure", "Sea Level Pressure"], variable: "pressureVal", defaultValue: "NA", unitKey: "units" },
    image: { keys: ["ImageData"], variable: "additionalImageURL", defaultValue: "NA", unitKey: "" },
    rawObs: {keys: ["Raw Observation", "RawObservation"], variable: "rawObservationVal", defaultValue: "NA", unitKey: "" },
    agl: {keys: ["Altitude"], variable: "aglVal", defaultValue: "NA", unitKey: "units" },
    average: {keys: ["AverageRate"], variable: "averageRate", defaultValue: "NA", unitKey: "" }
  };

  // Function to process the values
  function updateVariables(properties: any, useOptionalKey: boolean) {
    // Iterate through each key group in the mapping
    for (let group in keysToVariables) {
      let { keys, variable, defaultValue, unitKey } = keysToVariables[group];
      if(useOptionalKey) unitKey = "unit"
      for (let key of keys) {
        if (key in properties) {          
          let value = properties[key]["value"] ?? defaultValue;
          if (value !== "NA" && !isNaN(Number(value))) {
            if(group == 'pressure' && properties[key][unitKey].toLowerCase() == 'inhg'){              
              value = String(value) + (unitKey && properties[key][unitKey] ? " " + (unitMapping.has(properties[key][unitKey]) ? unitMapping.get(properties[key][unitKey]) : properties[key][unitKey])  : "");              
            }
            else if((group == 'temperature' || group == 'dewPoint') && properties[key][unitKey].toLowerCase() == 'c')
              value = String(value) + (unitKey && properties[key][unitKey] ? " " + (unitMapping.has(properties[key][unitKey]) ? unitMapping.get(properties[key][unitKey]) : properties[key][unitKey])  : "");
            else 
              value = String(Math.round(value)) + (unitKey && properties[key][unitKey] ? " " + (unitMapping.has(properties[key][unitKey]) ? unitMapping.get(properties[key][unitKey]) : properties[key][unitKey])  : "");
          }
      
          // Dynamically set the variable based on the group
          if (variable === "timeStampVal") timeStampVal = value;
          else if (variable === "temperatureVal") temperatureVal = value;
          else if (variable === "ceilingVal") ceilingVal = value;
          else if (variable === "windVal") windVal = value;
          else if (variable === "visibilityVal") visibilityVal = value;
          else if (variable === "sensoryTypeVal") sensoryTypeVal = value;
          else if (variable === "dewpointVal") dewpointVal = value;
          else if (variable === "pressureVal") pressureVal = value;
          else if (variable === "dirVal") dirVal = value;
          else if (variable === "additionalImageURL") additionalImageURL = value;
          else if (variable === "sensorIdVal") sensorIdVal = value;
          else if (variable === "rawObservationVal") rawObservationVal = value;
          else if (variable === "aglVal") aglVal = value;
          else if (variable === "averageRate") averageRate = value;
          prunedKeys.add(key)
          break;
        }
      }
    }
  }

  let unitMapping = new Map<string, string>()
  unitMapping.set("DEG", "°")
  unitMapping.set("DegN", "°")
  unitMapping.set("F", "°F")
  unitMapping.set("C", "°C")
  unitMapping.set("MPH", "mph")
  unitMapping.set("MPS", "mps")
  unitMapping.set("KTS", "kts")
  unitMapping.set("MILES", "mi")
  unitMapping.set("FT", "ft")
  unitMapping.set("M", "m")
  unitMapping.set("INHG", "inHg")
  unitMapping.set("HPA", "hPa")
  unitMapping.set("MB", "mb")
  unitMapping.set("KM", "km")
  unitMapping.set("SM", "SM")

  //Checking if the popup is for aviation product or weather sensor
  if("SensorType" in properties )  updateVariables(properties, false)
  else isAviationProduct = true


  if(windVal != "NA" && dirVal != "NA"){
    windVal += " / " + dirVal
  }
  
  //Extra processing for TAFS METARS and PIREPS  
  //TAF
  if(sensoryTypeVal == "NA" && "Raw TAF" in properties){
    sensoryTypeVal = "TAF"
    timeStampVal = properties["Issue Time"]
    rawObservationVal = properties["Raw TAF"]
    sensorIdVal = properties["ID"]
    prunedKeys.add("Issue Time")
    prunedKeys.add("Raw TAF")
    prunedKeys.add("ID")
  }
  //METAR
  //This is a special case where we have to display top bubbles as well 
  if(sensoryTypeVal == "NA" && "Report Type" in properties){
    isAviationProduct = false
    updateVariables(properties, true)
    sensoryTypeVal = properties["Report Type"]["value"]
    timeStampVal = properties["timestamp"]["value"]
    rawObservationVal = properties["Raw Observation"]["value"]
    sensorIdVal = properties["STID"]["value"]
    prunedKeys.add("timestamp")
    prunedKeys.add("Report Type")
    prunedKeys.add("Raw Observation")
    prunedKeys.add("STID")
  }
  //PIREP
  if(sensoryTypeVal == "NA" && "airepType" in properties){
    sensoryTypeVal = properties?.airepType?.value || "NA"
    timeStampVal = properties?.obsTime?.value || "NA"
    rawObservationVal = properties?.rawOb?.value || "NA"
    sensorIdVal = properties?.icaoId?.value || "NA"
    prunedKeys.add("obsTime")
    prunedKeys.add("airepType")
    prunedKeys.add("rawOb")
    prunedKeys.add("icaoId")
     prunedKeys.add("wake")
  }
  //

  let topElementsMeta : Array<Array<string>> = [
    ["Temperature", temperatureIcon, temperatureVal], 
    ["Dew Point", temperatureIcon, dewpointVal], 
    ["Wind", windIcon, windVal],
    ["Pressure", pressureIcon, pressureVal], 
    ["Ceiling", ceilingIcon, ceilingVal], 
    ["Visibility", visibilityIcon, visibilityVal]
  ]

  const capitalizeStr = (str) => {    
    const caps = str?.charAt(0).toUpperCase() + str?.slice(1)
    return caps
  }
  let keys: string[] = []
  const tableData = Object.entries(properties).flatMap(([key, value], index) => {    
    
    keys.push(key)    
    if (properties?.SensorType?.value == "Storm Water" && key == "Road Water Level" && typeof value.value === "number" && !Number.isNaN(value.value)){
      return [{ key, value: `${Math.abs(value.value)} ${value.units.toLowerCase()} ${value.value < 0 ? "below" : "above"} road level`, units: "" }]
    }
    if (Object.keys(properties).includes('Raw TAF') && key != 'Unit') {
      if (Object.keys(properties['Unit']).includes(key)) {
        return [{
          key,
          value,
          units: properties['Unit'][key],
        }]
      }
      return [{
        key,
        value,
      }]
    } else if (
      properties?.['Report Type'] &&
      properties?.['Report Type'].value == 'METAR'
    ) {
      return [{ key, value: value.value, units: value.unit }]
    } else if (
      (properties?.data && properties.data == 'AIRMET') ||
      properties.airSigmetType == 'SIGMET' ||
      properties.data == 'AIREP'
    ) {
      if (key == 'altitudeLow1') {
        return [{ key, value: value.toString() }]
      } else {
        return [{ key, value: value }]
      }
    } else if (properties.airepType) {
      if (key == 'altitudeLow1') {
        return [{ key, value: value.toString() }]
      }
      else if(key.toLowerCase() == "clouds"){        
        let cloudValues = [] 
        let keyList = []
        let index = 1       
        const COVER_ORDER = ['FEW', 'SCT', 'BKN', 'OVC'];

        const sorted = [...value?.value].sort(
          (a, b) => COVER_ORDER.indexOf(a.cover) - COVER_ORDER.indexOf(b.cover)
        );
        let cloudLabelMap = {
            'few' : 'Few', 
            'sct' : 'Scattered',
            'bkn' : 'Broken',
            'ovc' : 'Overcast',
            'skc' : 'Scattered'
        }
        for(let cloudVal of sorted){
          let {cover, base, top} = cloudVal
          const coverLabel = cover === "" ? "" : (cloudLabelMap[cover.toLowerCase()] ?? cover)
          const baseText = base > 0 ? `${base} ft` : ""
          const basePart = coverLabel && baseText ? `${coverLabel} at ${baseText}` : (coverLabel || baseText)
          const topPart = top > 0 ? `Top at ${top} ft` : ""
          cloudValues.push({
            "key": `Cloud Layer ${index}`,
            "value": [basePart, topPart].filter(Boolean).join(" with ")
          })
          keyList.push(`Cloud Layer ${index}`)
          index += 1
        }
        keys.push(...keyList)
        return cloudValues
      } 
      else {
        return [{ key, value: value.value }]
      }
    } else if (properties.Data?.StationID || properties) {
      if (!key.startsWith('CloudLayer')) {
        return [{ key, value: value.value, units: value.units }]
      } else {
        let heightAGL = null
        if (typeof value.value == 'string') {
          return [{ key, value: value.value, units: null }]
        }

        if (value.value.HeightAGL === null) {
          if (value.value.SkyCondition === 'clear') {
            //value.value.SkyCondition
            heightAGL = capitalizeStr(value.value.SkyCondition) //value.value.HeightAGL
          } else {
            heightAGL = capitalizeStr(value.value.SkyCondition) || "None"
          }
        } else {
          if (value.value?.SkyCondition === 'clear') {
            heightAGL = capitalizeStr(value.value?.SkyCondition) //value.value.HeightAGL
          } else if (value.value === 'No Ceilometer') {
            return [{ key, value: value.value, units: value.units }]
          } else {
            const val = value?.value?.HeightAGL?.value ?? "NA"
            const units = value?.value?.HeightAGL?.units ?? "NA"
            if(value.value.SkyCondition) heightAGL = `${capitalizeStr(value.value.SkyCondition)} at ${ typeof value === 'string' ? val.toString() : val } ${units.toLowerCase()}`
            else heightAGL = `${ typeof value === 'string' ? val.toString() : val } ${units.toLowerCase()}`
          }
        }
        return [{ key, value: heightAGL, units: value.units }]
      }
    } else {
      return [{ key, value: 'N/A' }]
    }
  })
  
  const order: string[] = [
    'Data',
    'timestamp',
    'Issue Time',
    'Valid Time From',
    'Valid Time To',
    'Valid Time',
    'Site',
    'STID',
    'ID',
    'Cloud Coverage 1',
    'Cloud Coverage 2',
    'Cloud Coverage 3',
    'Cloud Cover',
    'CloudLayer1',
    'CloudLayer2',
    'CloudLayer3',
    'Cloud Layer 1 Value',
    'Cloud Layer 2 Value',
    'Cloud Layer 3 Value',
    'WindSpeed',
    'Wind Speed',
    'Wind Direction',
    'WindDirection',
    'Visibility',
    'Temperature',
    'Dew Point Temperature',
    'DewpointTemperature',
    'Pressure',
    'Sea Level Pressure',
    'Raw TAF',
    'Raw Observation',
  ]
  keys = keys.filter((value) => !order.includes(value))
  const finalTableData = []

  //First filling all the keys with defined order
  for (const key of order) {
    const value = tableData.find((item) => item.key === key)
    if (value && !(prunedKeys.has(key))){
      finalTableData.push(value)
    }
  }

  //Filling rest of the keys
  for (const key of keys) {
    const value = tableData.find((item) => item.key === key)
    if (value && !(prunedKeys.has(key))) {
      finalTableData.push(value)
    }
  }
  
  function displayLabel(key: string): string | undefined {
    const labels: { [key: string]: string } = {
        "SkyConditionCloudLayerCovers": "Sky Condition",
        "SkyConditionCloudLayerHeights": "Cloud Base Height",
        "CloudThickness": "Cloud Thickness"
      };
    return labels[key];  
  }

  function layerMapping(key: string): string | undefined {
    const labels: { [key: string]: string } = {
        "0": "CLR",
        "1": "FEW",
        "2": "FEW",
        "3": "SCT",
        "4": "SCT",
        "5": "BKN",
        "6": "BKN",
        "7": "BKN",
        "8": "OVC"
    };
    return labels[key];  
  }

  function processRawObs(key: string): string[]{
    const newLineSplit = key.split('\n')
    if(newLineSplit.length > 1) return newLineSplit    
    const splitArr = key.split(" ")

    let currStatement = ""
    let processedArr = []
    for(let word of splitArr){
        if(currStatement.length > 0 && (word.startsWith("FM") || word.startsWith("BECMG") || word.startsWith("TEMPO"))){
            processedArr.push(currStatement.slice(0, -1))
            currStatement = ""
        } 
        currStatement += word + " "
    }
    if(currStatement.length > 0) processedArr.push(currStatement.slice(0, -1))
      
    return processedArr
  }
  function dataAvailable() {
    if(temperatureVal != "NA" || dewpointVal != "NA" || windVal != "NA" 
    || pressureVal != "NA" || ceilingVal != "NA" || visibilityVal != "NA"
    ) return true;
    return false;
  }

  let keyMapping = new Map<string, string>()
  keyMapping.set("DewPointTemperature","Dew Point") 
  keyMapping.set("OrientationMWS","Orientation MWS")

  const keyFormatting = (key : string ) => {
    if(key == "CL61Ceiling") return "Ceiling";
    if(keyMapping.has(key)) return keyMapping.get(key)
      let result = key[0]
      for (let ptr = 1; ptr < key.length; ptr++) {
        let c = key[ptr];
        if (c == c.toUpperCase()) {
            result += " ";
        }
        result += c;
    }
    return result
  }
  const cl61Headers = ["Property", "Layer 1", "Layer 2", "Layer 3", "Layer 4", "Layer 5"]  

  //Impementation for Sensor Alerts

  const eventLocalData = structuredClone(eventBodyTemplate)

  const [hazardList, setHazardList] = useState<Array<string>>([])
  const [currEventID, setEventID] = useState<string | null>(activeEvent)
  const defaultEmail = useSelector( (state: RootState) => state.user.email)
  const locationName = useSelector( (state: RootState) => state.user.defaultLocation?.name)
  const [toggleDisableState, settoggleDisableState] = useState<boolean>(false)
  const notificationPopup = useErrorStatus()
  const [customThresh, setCustomThresh] = useState({});
  const {setActiveEventsSensors } = useSensorEventContext();

  const hazardUnitMap  = {
    "ft" : "feet",
    "sm" : "SM",
    "kts": "kts",
    "F" : "degrees_f"
  }

  function getBufferPolygon(longitude: number, latitude: number){
    let radius = 0.0001; //degrees, 11 meters
    const bufferPolygon = [
      [longitude - radius, latitude - radius],
      [longitude - radius, latitude + radius],
      [longitude + radius, latitude + radius], 
      [longitude + radius, latitude - radius],
      [longitude - radius, latitude - radius],
    ]

    const geoJson = {
      "type" : 'Polygon',
      "coordinates" : [bufferPolygon]
    }
    return geoJson
  }

  function valueCheck(label: string, value: string): boolean {
    try {
      const conversions = {
        '°F-F' : x => x,
        '°C-F' : x => (x * 9 / 5) + 32,

        'mps-kts' : x => (x * 1.944),
        'kts-kts' : x => x,
        'mph-kts' : x => (x / 1.151),

        'ft-ft' : x => x,
        'm-ft' : x => (x * 3.281),
        
        'mi-sm' : x => x,
        'SM-sm' : x => x,
        'km-sm' : x => (x /  1.609)
      }

      let [rawVal, rawUnits] = value.split(" ");
      if(isNaN(Number(rawVal))) return false;

      if(label == "Temperature"){
        let [comp, val, unit] = customThresh["temperatureHighRange"]["currentHighRisk"]
        let unitKey = rawUnits + "-" + unit
        if(Number(val) <= conversions[unitKey](Number(rawVal))) return true;
      }
      else if(label == "Wind"){
        let [comp, val, unit] = customThresh["surfaceWind"]["currentHighRisk"]
        let unitKey = rawUnits + "-" + unit
        if(Number(val) <= conversions[unitKey](Number(rawVal))) return true;
      }
      else if(label == "Visibility"){
        let [comp, val, unit] = customThresh["visibility"]["currentHighRisk"]
        let unitKey = rawUnits + "-" + unit
        if(Number(val) >= conversions[unitKey](Number(rawVal))) return true;
      }
      else if(label == "Ceiling"){
        let [comp, val, unit] = customThresh["ceilingHeight"]["currentHighRisk"]
        let unitKey = rawUnits + "-" + unit
        if(Number(val) >= conversions[unitKey](Number(rawVal))) return true;
      }
      return false; 
    } catch (error) {
      return false
    }
  }

  const toggleEvent = async () => {    
    settoggleDisableState(true)
    //No event is curently active on this sensor
    if(currEventID == null){
      const customThresholds = await fetchUserThresholds()      
      const hazardKeysMap: { [key: string]: string } = {
          "Ceiling": "ceilingHeight",
          "Visibility": "visibility",
          "Surface Winds": "surfaceWind",
          "Surface Wind Gusts": "surfaceWindGust",
          "Temperature": "temperatureHighRange",
      };
      const hazardProductMap = structuredClone(sensorHazardProductMap)

      //Specifying geometry
      eventLocalData["message"]["geometry"] = getBufferPolygon(longitude, latitude)
      
      //Specifying time range
      const currUTCTime = new Date().toISOString();
      const now = new Date();
      const sixMonthsLater = new Date(now);
      sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
      const UTCTimePlus6Months = sixMonthsLater.toISOString();
      eventLocalData["message"]["properties"]["creationTime"] = currUTCTime
      eventLocalData["message"]["properties"]["startTime"] = currUTCTime
      eventLocalData["message"]["properties"]["endTime"] = UTCTimePlus6Months
      
      //Specifying Meta data
      eventLocalData["message"]["properties"]["eventName"] = `Event-${sensoryTypeVal}-${sensorIdVal}`
      eventLocalData["message"]["properties"]["externalEventName"] = `Event-${sensoryTypeVal}-${sensorIdVal}`
      eventLocalData["message"]["properties"]['notifiables'].push(defaultEmail)
      eventLocalData["message"]["location"] = locationName
      eventLocalData["message"]["sensorID"] = `${sensoryTypeVal}-${sensorIdVal}`
      
      //Specifying Products details
      Object.entries(hazardKeysMap).forEach(([key, val]) => {
        let [comp, value, units] = customThresholds[val]["currentHighRisk"] 
        let productName = hazardProductMap[key]
        let productBody = {
          "hazard" : productName,
          "parameters" : [
            {
              "thresholdUnits": hazardUnitMap[units],
              "comparison": comp,
              "distance": 0,
              "distanceUnits": "miles",
              "threshold": value,
            }
          ]
        }

        eventLocalData["message"]["properties"]["products"].push(productBody)
      })
      let response = await addSensorEvent(eventLocalData)
      setActiveEventsSensors(
        prev => ({
           ...prev,
            [`${sensoryTypeVal}-${sensorIdVal}`]: {
              "activeAlert": false, 
              "eventId": response.eventID
            }
        })
      )
      notificationPopup.addMessage(
        'Sensor Event created successfully.',
        200,
        'info',
        false,
        ErrorTimeouts.GENERAL
      )
      setEventID(response?.eventID)
    }
    //Delete the existing event
    else{
      await deleteSensorEvent(currEventID)
      setHazardList([])
      setEventID(null)
      setActiveEventsSensors(prev => {
        const { [`${sensoryTypeVal}-${sensorIdVal}`]: _, ...rest } = prev;
        return rest;
      });
      notificationPopup.addMessage(
        'Sensor Event deleted successfully.',
        200,
        'info',
        false,
        ErrorTimeouts.GENERAL
      )
    }
    settoggleDisableState(false)
  }

  //Init function for retrieving the hazard list
  useEffect(() => {
    if(activeEvent && sensoryTypeVal != "NA" && sensorIdVal != "NA"){
      getActiveHazards(`${sensoryTypeVal}-${sensorIdVal}`)
      .then((data) => {
        setHazardList(data)
      })
      .catch((err) => {
        console.error('Error fetching sensor alert data:', err);
      })

      fetchUserThresholds()
      .then((data) => {
        setCustomThresh(data)
      })
      .catch((err) => {
        console.error('Error fetching custom thresholds:', err);
      })
    }
  },[])

  return (
    <>
      {sensoryTypeVal === "CL61" || sensoryTypeVal == "CL31" ?
        <div className="flex flex-col gap-2 h-full justify-center w-full">
          <div className='flex gap-2 items-center'>
            <div className="header">
              Ceilometer Data
            </div>
            <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
              {sensoryTypeVal}
            </div>
            {
              sensorIdVal !== "NA" &&
              <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
                {sensorIdVal}
              </div>
            }
          </div>
          {
            timeStampVal !== "NA" && 
            <>
              <div className="labelStyling">
                  {timeStampVal}
              </div>
              <hr/>
            </>
          }
          {
            averageRate !== "NA" && 
            <>
              <div className="labelStyling">
                Averaging Rate : <b>{averageRate}</b>
              </div>
              <hr/>
            </>
          }
          <div className="rounded-lg border-2 border-gray-300 shadow-sm min-h-0 max-h-[60vh] overflow-y-auto" >
            <table className="min-w-full text-sm text-left text-gray-500 divide divide-y divide-gray-500">
            <thead className="bg-gray-100 text-black sticky top-0">
              <tr className='divide-x divide-gray-500  bg-[#FFFFFF]'>
                {
                  cl61Headers.map((item, key) =>(
                    <th key={key} scope="col" className="p-2 uppercase text-center font-semibold text-base">{item}</th>
                  ))
                }
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-500 h-max'>
              {finalTableData.map(
              (item, index) =>
                item.value !== undefined &&
                item.value !== '' &&
                item.value !== 'N/A' &&
                item.value !== 'NONE' &&
                (item.key !== "StationID" && item.key !== "timestamp" && item.key !== "SensorType" && item.key !== "VerticalVisibility" 
                  && item.key !== "SkyConditionTotalCloudCover" && item.key !== "Precipitation" && item.key !== "Fog" && item.key !== "CloudHeights" && item.key !== "CL61Ceiling"
                ) && (
                  <tr key={index} className="text-black divide-x divide-gray-500">
                   <th scope="row" className="bg-gray-100 px-6 py-2 font-semibold  whitespace-nowrap text-base">{displayLabel(item.key)}</th>
                    {
                      item.value.map((data:any) => (
                        <td className="text-base px-2 whitespace-nowrap text-center font-medium bg-[#F3F4F6] text-[#4B5563]">{item.key == "SkyConditionCloudLayerCovers" ? (data >= 0 ? layerMapping(data) : "-") : (data >= 0 ? `${Math.round(data)} ${item?.units?.toLowerCase()}` : "-") }</td>
                      ))
                    }
                  </tr>
                )
              )}
            </tbody>
            </table>
          </div>
        
          <div className="min-h-0 max-h-64 grid grid-cols-2 gap-1 overflow-y-auto p-2 border rounded-lg divide-y shadow-sm bg-[#FFFFFF]">
            {
              finalTableData.map(
                (item, index) => 
                  item.value !== undefined &&
                  item.value !== '' &&
                  item.value !== 'N/A' &&
                  item.value !== 'NONE' &&
                  (item.key == "VerticalVisibility" || item.key == "Precipitation"  || item.key == "Fog" || item.key == "CL61Ceiling" ) && 
                  (
                    <div key={index} className='p-2'>
                      <div className='labelStyling'>{keyFormatting(item.key)}</div>
                      <div className='valueStyling'>
                        {
                          (item.key !== "Precipitation"  && item.key !== "Fog") ? 
                          <>
                            {(typeof item.value === 'string' || (typeof item.value === 'number' && item.value >= 0)) ? Math.round(Number(item.value)) : "-"}{' '}
                            { item.value >= 0 ? item.units : ''}
                          </>
                          :
                          <>
                            {(typeof item.value === 'string' || (typeof item.value === 'number' && item.value == 1)) ? "Yes" : "No"}
                          </>
                        }
                      </div>
                    </div>
                  )
              )
            }
          </div>
        </div>
        :
        <div className="flex flex-col gap-2 h-full justify-center w-auto">
          <div className='flex justify-between'> 
            <div className='flex gap-2 items-center'>
              <div className="header">
                Weather Data
              </div>
              <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
                {sensoryTypeVal}
              </div>
              {
                sensorIdVal !== "NA" &&
                <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9]">
                  {sensorIdVal}
                </div>
              }
              {
                aglVal !== "NA" &&
                <div className='flex border shadow-sm p-1 rounded-full bg-[#F1F5F9]'>
                  <img src={heightIcon} alt="" />
                   <div className="font-semibold ">
                    {aglVal}
                  </div>
                </div>
              }
            </div>
            { 
              alertFeature && dataAvailable() && sensoryTypeVal != "Intellisense" &&
              <div title={!currEventID ? 'Click to begin alerting on this sensor.' : 'Click to disable alerting on this sensor.'} 
                className={`flex items-center justify-center ${hazardList?.length > 0 ? 'bg-[#FF5F15CC]' : (currEventID ? 'bg-twsBlue' : 'bg-white')} 
                border border-[#E2E8F0] rounded-lg w-8 h-8 flex hover:bg-[#E2E8F0]
                ${toggleDisableState ? 'pointer-events-none' : 'cursor-pointer'}`} onClick={toggleEvent}>
                {
                  toggleDisableState ? 
                  <svg aria-hidden="true" className="inline w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-gray-600 dark:fill-gray-300" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                  </svg> 
                  :
                  <svg xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="#F1F5F9" 
                    stroke="black" 
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    className="lucide lucide-triangle-alert-icon lucide-triangle-alert">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>
                  </svg>
                }
                
              </div>
            }
            
          </div>
          {
            timeStampVal !== "NA" && 
            <>
              <div className="labelStyling">
                  {timeStampVal}
              </div>
              <hr/>
            </>
          }
          {
            additionalImageURL !== "NA" &&
            <>
              <div className="subHeader">
                Most Recent Image
              </div> 
              <div className="custom shadow-sm">
                  <img src={additionalImageURL} alt="" />
              </div>
              <hr />
            </>
          }
          {
            !isAviationProduct && dataAvailable() &&
            <>
              <div className='grid grid-cols-2 gap-2'>
                {
                  topElementsMeta.map((item, index) => (
                    <>
                      <div className={`datafields flex gap-2 justify-left items-center ${hazardList.includes(item[0].toLowerCase()) && valueCheck(item[0], item[2])  ? 'bg-[#FF5F15]/20 animate-pulse-border' : 'bg-[#FFFFFF]'}`}>
                          <div className="w-8 h-8 flex">
                            <img src={item[1]} alt="Icon" />
                          </div>
                          <div className='flex flex-col'>
                            <div className='labelStyling'>{item[0]}</div>
                            <div className='valueStyling'> {item[2]} </div>
                          </div>
                      </div>
                    </>
                  ))
                }
              </div>
              <hr/>
            </>
          }
          {
            rawObservationVal !== "NA" &&
            <>
              <div className="subHeader w-full h-5 flex gap-2 items-center">
                Raw Data
              </div>
              <div className="min-h-0 max-h-64 p-2 border rounded-lg shadow-sm bg-[#FFFFFF] valueStyling overflow-y-auto">
                {
                  processRawObs(rawObservationVal).map((currStatement, index) => (
                    <>{currStatement}<br/></>
                  )) 
                }
              </div>
            </>
          }
          {
            finalTableData.length > 0 &&
            <>
              <div className="subHeader w-full h-5 flex gap-2 items-center">
                {!isAviationProduct && dataAvailable() && "Additional"} Parameters
              </div>
              <div className="min-h-0 max-h-64 grid grid-cols-2 gap-1 overflow-y-auto p-2 border rounded-lg divide-y shadow-sm bg-[#FFFFFF]">
                {
                  finalTableData.map(
                    (item, index) => 
                      item.value !== undefined &&
                      item.value !== '' &&
                      item.value !== 'N/A' &&
                      item.value !== 'NONE' && 
                      (
                        <div className='p-2'>
                          <div className='labelStyling'>{keyFormatting(item.key)}</div>
                          <div className='valueStyling'>
                            {
                              item.value != null && typeof item.value === "object" ? ( "Parsing..." ) :
                              (
                                <>
                                  {item.value !== null && item.value ? item.value : "None"}
                                  {item.value !== null && item.value && item.units !== undefined ? (unitMapping.has(item.units) ? unitMapping.get(item.units) : ' ' + item.units) : ''}
                                </>
                              )
                            }
                          </div>
                        </div>
                      )
                  )
                }
              </div>
            </>
          }
        </div>
      }
    </>
  )
}

export default MapPopupTable
